/**
 * The user's default shader provider and their pasted API keys, kept in this
 * browser's localStorage. Keys never leave the browser except to the provider
 * they belong to; they are not written to node state or page JSON.
 *
 * Every storage access is guarded: private windows and blocked site data make
 * localStorage throw, and that must never break the node.
 */

import { DEFAULT_LOCAL_BASE_URL, SHADER_PROVIDER_IDS } from './types'
import type { OpenRouterBilling, ShaderCodeModelHost, ShaderProviderConfig, ShaderProviderId, ShaderProviderSecrets } from './types'
import { hostServes } from './providers'

const DEFAULTS_KEY = 'genie.shader.provider.defaults'
const SECRETS_KEY = 'genie.shader.provider.secrets'

/**
 * `anthropic/claude-sonnet-4` is enabled and priced in Genie's catalogue (an
 * OpenRouter row whose endpointId is the slug), which is what the credits route
 * needs; see providers/genie-credits.ts.
 */
export const DEFAULT_SHADER_PROVIDER_CONFIG: ShaderProviderConfig = {
  provider: 'openrouter',
  billing: 'genie-credits',
  model: 'anthropic/claude-sonnet-4',
}

/** Starting points only: the settings panel lists each provider's live models. */
export const SUGGESTED_SHADER_MODELS: Record<ShaderProviderId, { id: string; label: string }[]> = {
  openrouter: [
    { id: 'anthropic/claude-sonnet-4', label: 'Claude Sonnet 4' },
    { id: 'openai/gpt-5.2', label: 'GPT-5.2' },
    { id: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { id: 'qwen/qwen3-235b-a22b', label: 'Qwen3 235B' },
  ],
  // xAI's models page (2026-09) recommends grok-4.6 for code.
  grok: [
    { id: 'grok-4.6', label: 'Grok 4.6' },
    { id: 'grok-4.20-0309-non-reasoning', label: 'Grok 4.20 (fast)' },
  ],
  local: [
    { id: 'qwen2.5:14b', label: 'Qwen 2.5 14B' },
    { id: 'qwen2.5-coder:14b', label: 'Qwen 2.5 Coder 14B' },
    { id: 'llama3.2', label: 'Llama 3.2' },
  ],
}

/** The model a provider starts with when the user switches to it. */
export const DEFAULT_SHADER_MODELS: Record<ShaderProviderId, string> = {
  openrouter: DEFAULT_SHADER_PROVIDER_CONFIG.model,
  grok: SUGGESTED_SHADER_MODELS.grok[0].id,
  local: SUGGESTED_SHADER_MODELS.local[0].id,
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

function readJson(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as unknown) : null
  } catch {
    return null
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Storage unavailable: the choice lasts for this page only.
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isProviderId(value: unknown): value is ShaderProviderId {
  return typeof value === 'string' && (SHADER_PROVIDER_IDS as readonly string[]).includes(value)
}

function isBilling(value: unknown): value is OpenRouterBilling {
  return value === 'genie-credits' || value === 'own-key'
}

/** Anything stored that is not a valid config falls back to the defaults. */
export function loadShaderProviderDefaults(): ShaderProviderConfig {
  const stored = readJson(DEFAULTS_KEY)
  if (!isRecord(stored) || !isProviderId(stored.provider)) return { ...DEFAULT_SHADER_PROVIDER_CONFIG }
  const provider = stored.provider
  const config: ShaderProviderConfig = {
    provider,
    model: typeof stored.model === 'string' && stored.model.trim() ? stored.model : DEFAULT_SHADER_MODELS[provider],
  }
  if (provider === 'openrouter') config.billing = isBilling(stored.billing) ? stored.billing : 'genie-credits'
  if (provider === 'local' && typeof stored.baseUrl === 'string' && stored.baseUrl.trim()) config.baseUrl = stored.baseUrl
  return config
}

export function saveShaderProviderDefaults(config: ShaderProviderConfig): void {
  // Only the non-secret fields, whatever the caller passed.
  const { provider, model, billing, baseUrl } = config
  writeJson(DEFAULTS_KEY, { provider, model, billing, baseUrl })
}

export function loadShaderProviderSecrets(): ShaderProviderSecrets {
  const stored = readJson(SECRETS_KEY)
  if (!isRecord(stored)) return {}
  const secrets: ShaderProviderSecrets = {}
  if (typeof stored.openrouter === 'string' && stored.openrouter) secrets.openrouter = stored.openrouter
  if (typeof stored.grok === 'string' && stored.grok) secrets.grok = stored.grok
  return secrets
}

/** Saves a key, or removes it with `null` or an empty string. */
export function saveShaderProviderSecret(provider: 'openrouter' | 'grok', key: string | null): void {
  const secrets = loadShaderProviderSecrets()
  const trimmed = key?.trim()
  if (trimmed) secrets[provider] = trimmed
  else delete secrets[provider]
  writeJson(SECRETS_KEY, secrets)
}

// ---------------------------------------------------------------------------
// Readiness
// ---------------------------------------------------------------------------

export type ShaderProviderReadiness = { ready: true } | { ready: false; reason: string; fix: 'add-key' | 'pick-model' | 'start-local' }

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Whether a generation can be attempted, and if not, what the user should do.
 * Whether Ollama is actually running is only known by trying.
 */
export function shaderProviderReadiness(
  config: ShaderProviderConfig,
  secrets: ShaderProviderSecrets,
  host?: ShaderCodeModelHost | null,
): ShaderProviderReadiness {
  if (!config.model.trim()) return { ready: false, reason: 'Pick a model to write the shader.', fix: 'pick-model' }
  if (config.provider === 'openrouter' && config.billing === 'own-key') {
    return { ready: false, reason: 'This saved shader used a personal key. Choose Magic Lamp with Genie credits before running.', fix: 'pick-model' }
  }
  if (hostServes(config, host)) return { ready: true }
  switch (config.provider) {
    case 'openrouter':
      return { ready: true }
    case 'grok':
      return { ready: false, reason: 'Grok account is unavailable here. Connect it in the desktop app or choose Magic Lamp.', fix: 'pick-model' }
    case 'local':
      return isHttpUrl(config.baseUrl?.trim() || DEFAULT_LOCAL_BASE_URL)
        ? { ready: true }
        : { ready: false, reason: 'Set the address Ollama is running at, e.g. http://localhost:11434.', fix: 'start-local' }
  }
}
